// Shared contract texts for all tipos (MT, RI, SI, PJ)
// Used by both HTML view (ContratoCliente.tsx) and PDF (pdfContrato.tsx)
// Based on legal texts from ORR-49, ORR-50, ORR-51, ORR-52, ORR-53

import { formatPrecioContrato } from './numero-a-letras'

export type TipoContrato = 'monotributo' | 'responsable_inscripto' | 'sucesion_indivisa' | 'persona_juridica'

export interface DatosContrato {
  tipo: TipoContrato
  // Cliente
  clienteNombre: string
  clienteCuit: string | null
  clienteDomicilio: string | null
  clienteCiudad: string | null
  clienteProvincia: string | null
  // Representante (SI y PJ)
  representanteNombre?: string | null
  representanteCuit?: string | null
  // Estudio
  estudioNombre: string
  estudioResponsable: string
  estudioCuit: string
  estudioMatricula?: string | null
  estudioDireccion: string
  estudioCiudad?: string | null
  estudioProvincia?: string | null
  // Contrato
  fechaAlta: string // ISO date
  precioImpositivo?: number | null
  precioLaboral?: number | null // if modulo laboral
  // Modulo laboral
  tieneModuloLaboral?: boolean
  /** Todos los servicios del cliente los paga un pagador: la cláusula de honorarios no lleva el ajuste. */
  honorariosDeTercero?: boolean
}

export interface Clausula {
  numero: string // 'PRIMERA', 'SEGUNDA', etc.
  titulo: string
  parrafos: string[] // body paragraphs
  items?: string[] // optional bullet list
  itemsExtra?: { titulo: string; items: string[] } // optional second list (e.g., modulo laboral)
}

// ==============================================================================
// ENCABEZAMIENTO
// ==============================================================================

export const generarEncabezamiento = (datos: DatosContrato): string => {
  const {
    tipo,
    clienteNombre,
    clienteCuit,
    clienteDomicilio,
    clienteCiudad,
    clienteProvincia,
    representanteNombre,
    representanteCuit,
    estudioNombre,
    estudioResponsable,
    estudioDireccion,
  } = datos

  const domicilioCompleto = [clienteDomicilio, clienteCiudad, clienteProvincia].filter(Boolean).join(', ')

  let parteCliente = ''

  if (tipo === 'monotributo' || tipo === 'responsable_inscripto') {
    // MT y RI: personas físicas
    parteCliente = `Entre el Sr./la Sra. ${clienteNombre}, CUIT ${clienteCuit ?? '-'}, con domicilio real en ${domicilioCompleto}`
  } else if (tipo === 'sucesion_indivisa') {
    const representanteStr = representanteNombre
      ? `, representada en este acto por el Sr./la Sra. ${representanteNombre}, CUIT ${representanteCuit ?? '-'}`
      : ''
    parteCliente = `Entre la Sucesión Indivisa ${clienteNombre}, CUIT ${clienteCuit ?? '-'}, con domicilio real en ${domicilioCompleto}${representanteStr}`
  } else if (tipo === 'persona_juridica') {
    const representanteStr = representanteNombre
      ? `, representada en este acto por el Sr./la Sra. ${representanteNombre}, CUIT ${representanteCuit ?? '-'}`
      : ''
    parteCliente = `Entre la sociedad ${clienteNombre}, CUIT ${clienteCuit ?? '-'}, con domicilio real en ${domicilioCompleto}${representanteStr}`
  }

  return `${parteCliente}, en adelante "El Cliente", y el estudio contable ${estudioNombre}, representado en este acto por ${estudioResponsable}, en su carácter de titular, con domicilio laboral en ${estudioDireccion}, en adelante "el profesional", se conviene en celebrar el presente convenio de trabajo de servicios profesionales, el que se regirá por las siguientes cláusulas:`
}

// ==============================================================================
// CLÁUSULA PRIMERA — Objeto y servicios
// ==============================================================================

const serviciosImpositivos: Record<TipoContrato, string[]> = {
  monotributo: [
    'Generación de VEP mensual de monotributo.',
    'Facturación de ventas.',
    'Presentación de declaración jurada de Ingresos Brutos.',
    'Recategorización semestral.',
    'Control de relación ventas/compras.',
  ],
  responsable_inscripto: [
    'Liquidación y presentación de declaración jurada de IVA e Ingresos Brutos.',
    'Control y presentación de Libro IVA Digital.',
    'Control y carga de facturación.',
    'Confección de libros IVA compra y venta.',
    'Generación de volantes de pago.',
  ],
  sucesion_indivisa: [
    'Liquidación y presentación de declaración jurada de IVA e Ingresos Brutos.',
    'Control y presentación de Libro IVA Digital.',
    'Control y carga de facturación.',
    'Confección de libros IVA compra y venta.',
    'Generación de volantes de pago.',
  ],
  persona_juridica: [
    'Presentación de declaración jurada de IVA.',
    'Presentación de declaración jurada de Ingresos Brutos.',
    'Presentación de declaración jurada de Sistema de Control de Retenciones (SICORE).',
    'Control y presentación de Libro IVA Digital.',
    'Control de facturación y confección de libros IVA compra y venta.',
    'Confección de libros Diario y Mayor General.',
  ],
}

const serviciosLaborales = [
  'Confección de recibo de sueldo.',
  'Presentación de declaración jurada SUSS (F931).',
  'Presentación de declaración jurada sindical.',
]

const generarClausulaPrimera = (datos: DatosContrato): Clausula => {
  const items = serviciosImpositivos[datos.tipo]
  const clausula: Clausula = {
    numero: 'PRIMERA',
    titulo: 'OBJETO',
    parrafos: [
      'El profesional se compromete a la realización específica de las siguientes tareas periódicas mensuales, sin que esta mención importe la negación de otras conducentes a los fines contratados:',
      'Módulo impositivo y contable:',
    ],
    items,
  }

  if (datos.tieneModuloLaboral) {
    clausula.itemsExtra = {
      titulo: 'Módulo laboral:',
      items: serviciosLaborales,
    }
  }

  clausula.parrafos.push(
    'Las declaraciones juradas y los volantes de pago correspondientes a las tareas enunciadas serán remitidas vía correo electrónico.'
  )

  return clausula
}

// ==============================================================================
// CLÁUSULA SEGUNDA — Duración
// ==============================================================================

const generarClausulaSegunda = (datos: DatosContrato): Clausula => {
  const fechaAlta = new Date(datos.fechaAlta + 'T00:00:00').toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return {
    numero: 'SEGUNDA',
    titulo: 'DURACIÓN',
    parrafos: [
      `La duración del presente contrato es por tiempo ilimitado, comenzando a regir el día ${fechaAlta}. Cualquiera de las partes podrá disolverlo, notificando tal determinación por medio fehaciente con quince (15) días de anticipación. Producida la notificación, las partes tendrán quince (15) días para el cumplimiento de todas las obligaciones recíprocas pendientes a la fecha de resolución.`,
    ],
  }
}

// ==============================================================================
// CLÁUSULA TERCERA — Documentación
// ==============================================================================

const clausulaTercera: Clausula = {
  numero: 'TERCERA',
  titulo: 'DOCUMENTACIÓN',
  parrafos: [
    'El cliente deberá proveer en tiempo y forma toda la documentación necesaria para el cumplimiento de los servicios prestados por el profesional. Dicho material deberá ser provisto en el domicilio de correo electrónico del profesional de forma oportuna, del uno (1) al diez (10) de cada mes, lo correspondiente a las tareas fiscales.',
  ],
}

// ==============================================================================
// CLÁUSULA CUARTA — Honorarios
// ==============================================================================

const generarClausulaCuarta = (datos: DatosContrato): Clausula => {
  const parrafos: string[] = [
    'El precio convenido para la realización de las tareas periódicas mensuales profesionales se establece en:',
  ]

  // Precio impositivo
  if (datos.precioImpositivo != null && datos.precioImpositivo > 0) {
    const precioFormateado =
      datos.tipo === 'monotributo'
        ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(
            datos.precioImpositivo
          )
        : formatPrecioContrato(datos.precioImpositivo)
    parrafos.push(`Por módulo impositivo la suma de ${precioFormateado}.`)
  } else {
    parrafos.push('Por módulo impositivo la suma que se acuerde entre las partes.')
  }

  // Precio laboral (if applicable)
  if (datos.tieneModuloLaboral) {
    if (datos.precioLaboral != null && datos.precioLaboral > 0) {
      const precioLaboralFormateado = formatPrecioContrato(datos.precioLaboral)
      parrafos.push(`Por módulo laboral la suma de ${precioLaboralFormateado}.`)
    } else {
      parrafos.push('Por módulo laboral la suma que se acuerde entre las partes.')
    }
  }

  if (!datos.honorariosDeTercero) {
    parrafos.push(
      'El precio convenido se actualizará un 10% cada 6 meses, en enero y julio, o lo indicado por el IPC (Índice de Precio al Consumidor) determinado por el INDEC para los últimos 6 meses transcurridos, el que fuese mayor.'
    )
  }
  parrafos.push(
    'Los honorarios deberán ser provistos en el domicilio laboral del profesional o en la cuenta bancaria indicada de forma oportuna, del uno (1) al diez (10) de cada mes, POR MES ADELANTADO.'
  )

  return {
    numero: 'CUARTA',
    titulo: 'HONORARIOS',
    parrafos,
  }
}

// ==============================================================================
// CLÁUSULAS QUINTA, SEXTA — Fijas
// ==============================================================================

const clausulaQuinta: Clausula = {
  numero: 'QUINTA',
  titulo: 'JUICIOS DE EJECUCIÓN FISCAL',
  parrafos: [
    'El precio convenido para la tramitación y descargo de los juicios de ejecución fiscal de cualquier organismo fiscalizador (ARCA, DGR, etc.) será analizado y presupuestado para cada caso particular, al momento de recibir el mismo.',
  ],
}

const clausulaSexta: Clausula = {
  numero: 'SEXTA',
  titulo: 'REQUERIMIENTOS Y SUMARIOS',
  parrafos: [
    'El precio convenido para la tramitación y descargo de requerimientos y sumarios de cualquier organismo fiscalizador (ARCA, DGR, etc.) será analizado y presupuestado para cada caso particular, al momento de recibir el mismo.',
  ],
}

// ==============================================================================
// CLÁUSULA SÉPTIMA — Presentaciones anuales (varía por tipo)
// ==============================================================================

const generarClausulaSeptima = (datos: DatosContrato): Clausula => {
  if (datos.tipo === 'persona_juridica') {
    return {
      numero: 'SÉPTIMA',
      titulo: 'PRESENTACIONES ANUALES',
      parrafos: [
        'En el precio convenido en la cláusula CUARTA no se incluyen las siguientes presentaciones anuales:',
      ],
      items: [
        'Impuesto a las ganancias persona jurídica.',
        'Confección y presentación de balances contables.',
        'Impuesto a las acciones o participaciones.',
      ],
      itemsExtra: {
        titulo: '',
        items: [
          'El precio para impuesto a las ganancias será el equivalente a un mes de honorarios actualizado al momento del vencimiento de la declaración jurada.',
          'El precio para confección y presentación de balances contables se ajustará a las horas que insuman la realización de las tareas, al 75% del valor según escala vigente del Consejo Profesional de Ciencias Económicas al momento de la prestación del servicio.',
        ],
      },
    }
  }

  // MT, RI, SI
  return {
    numero: 'SÉPTIMA',
    titulo: 'PRESENTACIONES ANUALES',
    parrafos: [
      'En el precio convenido en la cláusula CUARTA no se incluyen las siguientes presentaciones anuales:',
    ],
    items: ['Impuesto a las ganancias personas físicas.', 'Impuesto sobre los bienes personales.'],
    itemsExtra: {
      titulo: '',
      items: [
        'El precio para dichas tareas será el equivalente a un mes de honorarios actualizado al momento del vencimiento de la declaración jurada.',
      ],
    },
  }
}

// ==============================================================================
// CLÁUSULAS OCTAVA a DÉCIMOPRIMERA — Fijas
// ==============================================================================

const clausulaOctava: Clausula = {
  numero: 'OCTAVA',
  titulo: 'SERVICIOS NO MENCIONADOS',
  parrafos: [
    'El precio de los servicios prestados que no hayan sido mencionados en las cláusulas PRIMERA, CUARTA, QUINTA, SEXTA y SÉPTIMA, se ajustarán a las horas que insuman la realización de las tareas, al 75% del valor según escala vigente del Consejo Profesional de Ciencias Económicas al momento de la prestación del servicio.',
    'Queda a criterio de EL PROFESIONAL ajustar, y notificar previo inicio de este servicio especial, si los honorarios deberán ser pactados de una forma distinta a lo indicado en el párrafo anterior.',
  ],
}

const clausulaNovena: Clausula = {
  numero: 'NOVENA',
  titulo: 'ÉTICA PROFESIONAL',
  parrafos: [
    'Los servicios se prestarán a ciencia y conciencia del profesional, el que deberá actuar dentro de las prescripciones éticas y legales que hacen a su disciplina y oficio profesional, pero siempre teniendo en mira y finalidad el objeto del presente contrato y de la contratación de sus servicios efectuada por el locatario. Si en el curso de su labor surgieren imposibilidades o incompatibilidades legales respecto del ejercicio profesional contratado, el profesional las hará saber al cliente a fin de buscar los medios idóneos para obviar las mismas o para proceder a la resolución de este contrato —si fuera imposible dar solución satisfactoria a las imposibilidades o dificultades surgidas— en cuyo caso el precio convenido pasará a ser el proporcional a los servicios cumplidos.',
  ],
}

const clausulaDecima: Clausula = {
  numero: 'DÉCIMA',
  titulo: 'RESPONSABILIDAD SOBRE DOCUMENTACIÓN',
  parrafos: [
    'Se deja expresamente aclarado que el profesional efectuará las labores ajustándose estrictamente a los comprobantes que el locatario le provea. Por lo tanto, de existir la falsedad en las declaraciones juradas, será de exclusiva responsabilidad del cliente.',
  ],
}

const clausulaDecimoprimera: Clausula = {
  numero: 'DÉCIMOPRIMERA',
  titulo: 'CONFIDENCIALIDAD',
  parrafos: [
    'El profesional no podrá comunicar a personal alguno ni a terceros la información no publicada o de carácter confidencial de que se haya tenido conocimiento, con motivo de la ejecución de sus obligaciones emanadas de su servicio profesional, salvo que así lo requiera su cometido o que el cliente lo hubiera autorizado expresamente para hacerlo. Esta obligación de reserva o confidencialidad seguirá en vigor aún después del vencimiento del plazo de disolución del convenio de trabajo, haciéndose responsable el profesional de los daños y perjuicios que pudiera irrogar la difusión de datos o informes no publicados.',
  ],
}

// Sin CUIT cargado en la configuración del estudio, el párrafo nombra al profesional sin número.
const generarClausulaDecimosegunda = (datos: DatosContrato): Clausula => ({
  numero: 'DÉCIMA SEGUNDA',
  titulo: 'EXONERACIÓN DE RESPONSABILIDAD, CUMPLIMIENTO TRIBUTARIO Y DELEGACIÓN DE SERVICIOS',
  parrafos: [
    'Queda expresamente convenido que todas las obligaciones impositivas, previsionales y formales cuya gestión se delega al profesional, corren por exclusiva cuenta y orden del cliente. En consecuencia, el profesional queda exonerado de toda responsabilidad patrimonial o legal ante cualquier eventual error, omisión o contingencia material que pudiera suscitarse en la liquidación o proceso de las tareas encomendadas. Asimismo, se deja constancia de que es de responsabilidad exclusiva e indelegable del cliente: (a) la provisión de fondos y el pago en término de cualquier deuda impositiva o tributaria; (b) asumir las consecuencias, multas o recargos derivados de la falta de presentación de declaraciones juradas; y (c) la adhesión, revisión, control y lectura periódica de todas las notificaciones o requerimientos cursados a su Domicilio Fiscal Electrónico (AFIP/ARCA, DGR, u otros organismos fiscalizadores). Cualquier perjuicio derivado del incumplimiento de estas obligaciones recaerá íntegramente sobre el cliente.',
    `El cliente podrá delegar en el profesional, mediante el Administrador de Relaciones de Clave Fiscal de ARCA o el mecanismo equivalente de otros organismos, los servicios necesarios para prestar las tareas encomendadas, incluida la consulta de las comunicaciones de su Domicilio Fiscal Electrónico. La delegación se realiza a favor de la CUIT ${datos.estudioCuit ? datos.estudioCuit : 'del profesional'} y tiene por único fin facilitar las tareas convenidas en este contrato. La delegación no transfiere al profesional la titularidad de ninguna obligación fiscal ni las responsabilidades del cliente detalladas en esta cláusula, que siguen siendo exclusivas del cliente. En particular, la consulta que realice el profesional no reemplaza la obligación del cliente de leer y controlar las notificaciones de su Domicilio Fiscal Electrónico. El cliente podrá revocar la delegación en cualquier momento, y deberá comunicarlo al profesional por escrito.`,
  ],
})

// ==============================================================================
// EXPORT: Main function
// ==============================================================================

export const generarClausulas = (datos: DatosContrato): Clausula[] => {
  return [
    generarClausulaPrimera(datos),
    generarClausulaSegunda(datos),
    clausulaTercera,
    generarClausulaCuarta(datos),
    clausulaQuinta,
    clausulaSexta,
    generarClausulaSeptima(datos),
    clausulaOctava,
    clausulaNovena,
    clausulaDecima,
    clausulaDecimoprimera,
    generarClausulaDecimosegunda(datos),
  ]
}

// ==============================================================================
// HELPERS
// ==============================================================================

export const getTipoContratoLabel = (tipo: TipoContrato): string => {
  const labels: Record<TipoContrato, string> = {
    monotributo: 'Monotributista',
    responsable_inscripto: 'Responsable Inscripto',
    sucesion_indivisa: 'Sucesión Indivisa',
    persona_juridica: 'Persona Jurídica',
  }
  return labels[tipo]
}

/**
 * Map legacy tipo_contrato values for backwards compatibility
 */
export const normalizarTipoContrato = (tipo: string): TipoContrato => {
  if (tipo === 'sociedad') return 'persona_juridica'
  if (tipo === 'monotributo' || tipo === 'responsable_inscripto' || tipo === 'sucesion_indivisa' || tipo === 'persona_juridica') {
    return tipo
  }
  return 'monotributo'
}
